import React, { useState, useEffect } from 'react';
import { X, Download, Share, PlusCircle } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const InstallPrompt: React.FC = () => {
  const [showAndroidPrompt, setShowAndroidPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if the app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isIOSBrowser = isIOS && isSafari;

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show our custom install prompt
      setShowAndroidPrompt(true);
    };

    // Check if we should show iOS prompt
    const checkIOSPrompt = () => {
      // Only show iOS prompt if on iOS Safari and not already installed
      if (isIOSBrowser && !isInstalled) {
        // Check if user has dismissed the prompt before
        const hasIOSPromptBeenDismissed = localStorage.getItem('iosPromptDismissed');
        if (!hasIOSPromptBeenDismissed) {
          // Wait a bit before showing the prompt
          setTimeout(() => {
            setShowIOSPrompt(true);
          }, 2000);
        }
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    checkIOSPrompt();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    // We've used the prompt, and can't use it again, so clear it
    setDeferredPrompt(null);
    
    // Hide our custom install prompt
    setShowAndroidPrompt(false);
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
  };

  const dismissAndroidPrompt = () => {
    setShowAndroidPrompt(false);
  };

  const dismissIOSPrompt = () => {
    setShowIOSPrompt(false);
    // Remember that user dismissed the prompt
    localStorage.setItem('iosPromptDismissed', 'true');
  };

  // Don't show anything if app is already installed
  if (isInstalled) {
    return null;
  }

  return (
    <>
      {/* Android Install Prompt */}
      {showAndroidPrompt && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 p-4 shadow-lg z-50 border-t border-gray-200 dark:border-gray-700 transition-all">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full">
                <Download className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">Install BangaAI</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">Add to your home screen for quick access</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={dismissAndroidPrompt}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
              <button
                onClick={handleInstallClick}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Install
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iOS Install Prompt */}
      {showIOSPrompt && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 p-4 shadow-lg z-50 border-t border-gray-200 dark:border-gray-700 transition-all">
          <div className="relative">
            <button 
              onClick={dismissIOSPrompt}
              className="absolute right-0 top-0 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex flex-col items-center text-center mb-4 pt-2">
              <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full mb-3">
                <PlusCircle className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-medium text-lg text-gray-900 dark:text-white mb-2">
                Install BangaAI on your iPhone
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 max-w-xs mx-auto">
                Install this app on your home screen for quick access
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="mt-1">
                  <Share className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Step 1:</strong> Tap the <strong>Share</strong> button in your browser
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="mt-1">
                  <PlusCircle className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Step 2:</strong> Scroll down and tap <strong>Add to Home Screen</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};